// API Configuration
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.tenexchn.com/api'

/* Sports live stream — the player site's own value, declared the same way it
 * declares it (frontend/src/config.js `SPORTS_STREAM_BASE`, value in
 * frontend/.env):
 *
 *     VITE_SPORTS_STREAM_BASE=https://stream-s-43.uhdmovies.online/sports-stream?id=
 *
 * and the iframe src is that base + the match's gmid — checked live:
 * `…/sports-stream?id=462855645` answers 200 with the player page.
 *
 * The reference admin asks its server instead (`game/tv` → `{url}`); our
 * equivalent proxy (`POST /user/cricket/live-stream` → upstream `tv_url`) times
 * out at 6 s today, so the static base is what actually works — same as the
 * player site. Add the line above to `admin/.env` (yours to edit) and this
 * fallback stops being used.
 */
export const SPORTS_STREAM_BASE =
  import.meta.env.VITE_SPORTS_STREAM_BASE || 'https://stream-s-43.uhdmovies.online/sports-stream?id='

export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/admin/staff/login',
    OWNER_LOGIN: '/admin/login-owner',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',

    STAFF_UPDATE_PASSWORD: '/admin/staff-update-password',
    OWNER_UPDATE_PASSWORD: '/admin/owner/update-password',
    ACCEPT_RULES_OWNER: '/admin/accept-rules',
    ACCEPT_RULES_STAFF: '/admin/staff/accept-rules',
    UPDATE_PLATFORM_CONFIG: '/admin/update-platform-configurations',

    // 2FA Endpoints
    VERIFY_2FA_STAFF: '/admin/staff/verify-2fa',
    VERIFY_2FA_OWNER: '/admin/owner/verify-2fa',
    // Re-send the login OTP — the 2FA screen calls this on a refresh
    RESEND_2FA_STAFF: '/admin/staff/resend-2fa-otp',
    RESEND_2FA_OWNER: '/admin/owner/resend-2fa-otp',
    GENERATE_2FA_LINK: '/2fa/telegram/generate-link-code',
    VERIFY_PASSWORD: '/2fa/telegram/verify-password',
    SEND_DISABLE_2FA_CODE: '/2fa/telegram/send-disable-code',
    DISABLE_2FA: '/2fa/telegram/disable-2fa',
  },
  USER: {
    PROFILE: '/admin/staff/profile',
    OWNER_PROFILE: '/admin/profile',
    ALL_DETAILS: '/admin/users/all-details',
    SEARCH_BY_USERNAME: '/admin/search-by-username', // userRoutes mounts it under /admin, not /admin/users

    // One account's own profile details (More → Additional Information).

    ACCOUNT_DETAILS: (accountType, id) => `/admin/users/account-details/${accountType}/${id}`,
    CREATE_USER: '/admin/users/create-under-parent',
    CREATE_STAFF: '/admin/staff/create-staff',
    CHECK_USERNAME: '/admin/check-username',
    UPDATE_STATUS_PASSWORD: '/admin/staff-user-update',
  },
  D99: {
    NET_EXPOSURE: '/d99/net-exposure',
  },
  REPORTS: {
    CURRENT_BETS: '/admin/reports/current-bets',
    MARKET_ANALYSIS: '/admin/reports/market-analysis',
    MARKET_ANALYSIS_DASHBOARD: '/admin/reports/market-analysis/dashboard',
    TOTAL_PROFIT_LOSS: '/admin/reports/total-profit-loss',
    USER_BET_HISTORY: '/admin/reports/user-bet-history',
  },
  // Wallet / credit — "Credit Activity" modal (CR column pe click)
  WALLET: {
    DEPOSIT_ADD: '/admin/wallet/deposit/add',   // Credit Deposit tab
    WITHDRAW: '/admin/wallet/withdraw',         // Credit Withdraw tab
    CREDIT_HISTORY: '/admin/ledger/user/history', // Credit History tab
  },
  SECURE_AUTH: {
    // Owner-only bot configuration (username + token)
    BOT: '/admin/secure-auth/bot',
    // The logged-in owner/staff turning secure auth on or off for their OWN login
    SELF_VERIFY_PASSWORD: '/admin/secure-auth/verify-password',
    SELF_GENERATE_LINK_CODE: '/admin/secure-auth/generate-link-code',
    SELF_SEND_DISABLE_CODE: '/admin/secure-auth/send-disable-code',
    SELF_DISABLE: '/admin/secure-auth/disable',
    // Google Authenticator (TOTP) for the logged-in owner/staff
    SELF_GOOGLE_SETUP: '/admin/secure-auth/google/setup',
    SELF_GOOGLE_ENABLE: '/admin/secure-auth/google/enable',
    SELF_GOOGLE_DISABLE: '/admin/secure-auth/google/disable',
    // Secure Auth Verification App (Android) — pairing code + disable
    SELF_ANDROID_PAIR: '/admin/secure-auth/android/pair',
    SELF_ANDROID_DISABLE: '/admin/secure-auth/android/disable',
  },
  CAROUSEL: {
    CREATE: '/admin/carousel/create',
    LIST: '/admin/carousel/list',
    REORDER: '/admin/carousel/reorder',
    UPDATE: '/admin/carousel', // + /:carousel_id
    DELETE: '/admin/carousel', // + /:carousel_id
  },
  FIXTURES: '/admin/fixtures', // collector-fed upcoming fixtures (header ticker + owner Fixtures page)
  POSTER: {
    GET: '/admin/poster',
    UPDATE: '/admin/poster',
  },
  BANNER_SYNC: {
    STATUS: '/admin/banner-sync/status', // 4-hourly carousel + poster mirror from the reference site
    RUN: '/admin/banner-sync/run',
  },
  CASINO_MANAGE: {
    LIST: '/admin/casino-manage/list',
    CREATE: '/admin/casino-manage/create',
    UPLOAD: '/admin/casino-manage/upload',
    UPDATE: '/admin/casino-manage', // + /:id
  },
  // Home Sliders — the mobile home page's "Our Casino" / "New Games" strips
  HOME_SLIDER: {
    LIST: '/admin/home-slider/list',
    CREATE: '/admin/home-slider/create',
    UPLOAD: '/admin/home-slider/upload',
    REORDER: '/admin/home-slider/reorder',
    UPDATE: '/admin/home-slider', // + /:id
    DELETE: '/admin/home-slider', // + /:id
  },
  // Owner-only Buffer Time + Bet Limits (sports & casino) + Max Odds (sports)
  BETTING_CONFIG: {
    BUFFER_SPORTS: '/admin/betting-config/buffer/sports',       // GET grid, POST one cell, DELETE league override
    BUFFER_SPORTS_BULK: '/admin/betting-config/buffer/sports/bulk',
    BUFFER_CASINO: '/admin/betting-config/buffer/casino',
    LIMITS_INPLAY: '/admin/betting-config/bet-limits/inplay',
    LIMITS_EVENT: '/admin/betting-config/bet-limits/event',   // + /:event_id (GET), /:id (DELETE)
    LIMITS_CASINO: '/admin/betting-config/bet-limits/casino', // + /:id (DELETE)
    MAX_ODDS_SPORTS: '/admin/betting-config/max-odds/sports',
    AUTO_VOID: '/admin/betting-config/auto-void',                 // GET, PUT (owner only)
    AUTO_VOID_TRACKER: '/admin/betting-config/auto-void/tracker', // GET (owner only)
    RESULT_LINKS: '/admin/betting-config/auto-void/links',         // GET, POST, PUT/:id, DELETE/:id (owner only)
    VIRTUAL_LIMITS: '/admin/betting-config/virtual-limits',       // GET, PUT (owner only)
  },
  // Owner-only sports win commission per sport x league x market.
  // SPORTS: GET the grid, POST one cell, DELETE one league override
  // (back to inherit). BULK: POST every dirty cell in one call.
  COMMISSION_CONFIG: {
    SPORTS: '/admin/commission-config/sports',
    SPORTS_BULK: '/admin/commission-config/sports/bulk',
  },
  // Third Party Game Limit — owner-only per-bet stake cap for JS games,
  // inherited down the hierarchy.
  THIRD_PARTY_LIMIT: {
    LIST: '/admin/third-party-limit/list',
    SET: '/admin/third-party-limit/set',
  },
  TABLE_CASINO: {
    LIST: '/admin/table-casino/list',
    LOCKS: '/admin/table-casino/locks',
    LOCK: '/admin/table-casino/lock',
  },
  BETLOCK: {
    MARKET_LOCK_CHILDREN: '/admin/betlock/market-locks',
    TOGGLE_MARKET_LOCK: '/admin/betlock/toggle-market-lock',
    BULK_TOGGLE_MARKET_LOCK: '/admin/betlock/bulk-toggle-market-lock',
  },
  SPORTS_DATA: '/user/cricket/all-data',
  GET_SPORTS_TREE: '/user/all/sports/tree',
  // the same tree, ordered server-side (see server/services/SportsEventsService.js)
  GET_SPORTS_EVENTS: '/user/all/sports/events',
  GET_SPORT_DATA: '/user/cricket/all-data',
  GET_MATCH_PRIVATE_DATA: '/user/cricket/get-match-private-data',
  GET_MARKET_LIST: '/user/cricket/market-list',
  GET_SPORTS_SCORE_CARD: '/user/cricket/score-card',
  // match header/meta incl. `gtv` (score-animation id) and `scard` — the
  // all-data rows do NOT carry them
  GET_SPORTS_GAME_DETAIL: '/user/v1/sports-gamedetailPrivate',
  // the score-animation url, resolved server-side and returned UNUSED
  GET_SCORE_URL: '/user/cricket/score-url',
  INTERNALSETTLE: {
    MOMATCHES: '/internalsettle/momatches',
    FANMATCHES: '/internalsettle/fanmatches',
    DECLARERESULT: '/internalsettle/declareresult',
    VOID: '/internalsettle/void',
    OPEN_BETS: '/internalsettle/open-bets',
    VOID_SINGLE_BET: '/internalsettle/void-single-bet',
    SETTLED_MARKETS: '/internalsettle/settled-markets',
    SETTLED_BETS: '/internalsettle/settled-bets',
    VOID_MARKET_AFTER_SETTLEMENT: '/internalsettle/void-market-after-settlement',
    VOID_BET_AFTER_SETTLEMENT: '/internalsettle/void-bet-after-settlement',
    // Casino — unsettled (stuck) bets aur unka void
    CASINO_OPEN_BETS: '/internalsettle/casino-open-bets',
    VOID_CASINO_BETS: '/internalsettle/void-casino-bets',
    CASINO_SETTLED_BETS: '/internalsettle/casino-settled-bets',
    VOID_SETTLED_CASINO_BET: '/internalsettle/void-settled-casino-bet',
  },
  // Executive (multi-login) accounts — server mounts these at /api/executive
  MULTI_LOGIN: {
    LOGIN: '/executive/login',
    CREATE: '/executive/create',
    LIST: '/executive/list',
    BASE: '/executive', // + /:id (PUT), /:id/active, /:id/password, /:id (DELETE)
  },
  JSGAMES: {
    CATEGORIES: '/jsGames/games/categories',
    CATEGORY_PRIORITY: '/jsGames/games/category', // + /:category/priority
    ADMIN_GAMES: '/jsGames/admin/games',          // GET list, PUT /:id
    ADMIN_VENDORS: '/jsGames/admin/vendors',
    ADMIN_CATEGORY: '/jsGames/admin/category',    // + /:category/providers[/:provider[/categories[/:slug]]]
                                                  // and /:category/games/:id/provider
  },
}
